import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  Fragment,
} from "react";
import {
  Container,
  Box,
  Title,
  Text,
  Stack,
  Modal,
  Button,
  Group,
  Badge,
  Textarea,
  TextInput,
  Select,
  Paper,
  Divider,
  ActionIcon,
  Tooltip,
  Code,
  Alert,
  Table,
  Loader,
  Center,
  Pagination,
} from "@mantine/core";
import {
  IconPlus,
  IconAlertCircle,
  IconCheck,
  IconX,
  IconSearch,
  IconFilter,
  IconRefresh,
  IconChevronDown,
  IconChevronRight,
  IconEdit,
  IconEye,
} from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { useDisclosure, useDebouncedValue } from "@mantine/hooks";
import { useForm } from "@mantine/form";
import { RichTextEditor } from "@mantine/tiptap";
import { useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import type { Editor } from "@tiptap/core";
import {
  useInternalGrossCodes,
  useCreateInternalGrossCode,
  useUpdateInternalGrossCode,
  useApproveInternalGrossCode,
  useRejectInternalGrossCode,
} from "../../../hooks/admin/useInternalGrossCodes";
import { useAuthStore } from "../../../store/auth.store";
import {
  parseVariables,
  replaceVariables,
} from "../../../services/internalGrossCode.service";
import { required, scrollToFirstError } from "../../../utils/formValidation";
import type {
  InternalGrossCodeWithUser,
  InternalGrossCodeStatus,
} from "../../../types/grossing";
import AdminFragmentCountInput, {
  parseAdminFragmentCountDraft,
} from "../../../components/admin/grosscodes/AdminFragmentCountInput";
import { getFragmentCountDisplayValue } from "../../../utils/fragmentCount";

type StatusFilter = "all" | InternalGrossCodeStatus;

interface GrossCodeFormValues {
  code: string;
  description: string;
  scenario_description: string;
  fragment_count_draft: string;
}

interface RejectFormValues {
  reason: string;
}

const ITEMS_PER_PAGE = 20;

// Helper to strip HTML tags for plain text preview
const stripHtml = (html: string): string => {
  const doc = new DOMParser().parseFromString(html, "text/html");
  return doc.body.textContent || "";
};

// Available variables for gross code templates (from C-1224 spec)
const AVAILABLE_VARIABLES = [
  { name: "specimen_count", description: "Number of specimens", example: "3" },
  {
    name: "specimen_letter",
    description: "Specimen designation",
    example: "A",
  },
  { name: "source", description: "Tissue source", example: "Colon" },
  { name: "subsite", description: "Specific location", example: "Ascending" },
  {
    name: "collection_method",
    description: "How collected",
    example: "Polypectomy",
  },
  { name: "size_cm", description: "Size in centimeters", example: "2.5" },
  { name: "size_mm", description: "Size in millimeters", example: "25" },
  { name: "cassette_count", description: "Number of cassettes", example: "2" },
  { name: "block_count", description: "Number of blocks", example: "2" },
  { name: "color", description: "Tissue color", example: "tan-pink" },
  { name: "consistency", description: "Tissue consistency", example: "soft" },
];

// Generate sample data for preview
const SAMPLE_DATA: Record<string, string> = AVAILABLE_VARIABLES.reduce(
  (acc, v) => ({ ...acc, [v.name]: v.example }),
  {}
);

export default function InternalGrossCodesPage() {
  const { profile } = useAuthStore();
  const userId = profile?.id;

  // Modal states
  const [opened, { open, close }] = useDisclosure(false);
  const [
    toggleActiveModalOpened,
    { open: openToggleActiveModal, close: closeToggleActiveModal },
  ] = useDisclosure(false);
  const [
    reviewModalOpened,
    { open: openReviewModal, close: closeReviewModal },
  ] = useDisclosure(false);

  // Page state
  const [editingCode, setEditingCode] =
    useState<InternalGrossCodeWithUser | null>(null);
  const [togglingCode, setTogglingCode] =
    useState<InternalGrossCodeWithUser | null>(null);
  const [reviewingCode, setReviewingCode] =
    useState<InternalGrossCodeWithUser | null>(null);
  const [reviewMode, setReviewMode] = useState<"idle" | "rejecting">("idle");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const [page, setPage] = useState(1);
  const [showPreview, setShowPreview] = useState(false);

  // Build filters for API call
  const apiFilters = useMemo(
    () => ({
      status: statusFilter !== "all" ? statusFilter : undefined,
      search: debouncedSearch.trim() || undefined,
      includeInactive: true, // Show all records in admin view
    }),
    [statusFilter, debouncedSearch]
  );

  // Fetch data with server-side filtering
  const {
    data: filteredCodes = [],
    isLoading,
    refetch,
  } = useInternalGrossCodes(apiFilters);

  // Mutations
  const createMutation = useCreateInternalGrossCode();
  const updateMutation = useUpdateInternalGrossCode();
  const approveMutation = useApproveInternalGrossCode();
  const rejectMutation = useRejectInternalGrossCode();

  // Main form
  const form = useForm<GrossCodeFormValues>({
    initialValues: {
      code: "",
      description: "",
      scenario_description: "",
      fragment_count_draft: "",
    },
    validate: {
      code: required("Code is required"),
      description: (value) => {
        const plainText = stripHtml(value || "").trim();
        return !plainText ? "Description is required" : null;
      },
      fragment_count_draft: (value) =>
        parseAdminFragmentCountDraft(value).error,
    },
  });

  // Reject form
  const rejectForm = useForm<RejectFormValues>({
    initialValues: {
      reason: "",
    },
    validate: {
      reason: required("Rejection reason is required"),
    },
  });
  const [rejectReasonError, setRejectReasonError] = useState<string | null>(
    null
  );

  // Rich Text Editor for description
  const editor = useEditor({
    extensions: [
      StarterKit,
      Placeholder.configure({
        placeholder:
          "Enter gross description template. Use {{variable_name}} for dynamic fields...",
      }),
    ],
    content: form.values.description || "",
    onUpdate: ({ editor: updateEditor }: { editor: Editor | null }) => {
      if (!updateEditor) return;
      const html = updateEditor.getHTML();
      form.setFieldValue("description", html);
    },
  }) as Editor | null;

  // Rich Text Editor for rejection reason
  const rejectEditor = useEditor({
    extensions: [
      StarterKit,
      Placeholder.configure({
        placeholder: "Explain why this template is being rejected...",
      }),
    ],
    content: "",
    onUpdate: ({ editor: updateEditor }: { editor: Editor | null }) => {
      if (!updateEditor) return;
      const html = updateEditor.getHTML();
      rejectForm.setFieldValue("reason", html);
      if (html && html !== "<p></p>") {
        setRejectReasonError(null);
      }
    },
  }) as Editor | null;

  // Sync editor content when editing an existing code
  useEffect(() => {
    if (editor && editingCode) {
      const currentHtml = editor.getHTML();
      if (currentHtml !== editingCode.description) {
        editor.commands.setContent(editingCode.description || "");
      }
    }
  }, [editingCode, editor]);

  // Reset editor when modal closes
  useEffect(() => {
    if (!opened && editor) {
      editor.commands.setContent("");
    }
  }, [opened, editor]);

  // Reset reject editor when review modal closes or mode changes back to idle
  useEffect(() => {
    if (reviewMode === "idle" && rejectEditor) {
      rejectEditor.commands.setContent("");
      rejectForm.setFieldValue("reason", "");
      setRejectReasonError(null);
    }
  }, [reviewMode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Pagination (client-side for simplicity)
  const totalPages = Math.ceil(filteredCodes.length / ITEMS_PER_PAGE);
  const paginatedData = useMemo(() => {
    const start = (page - 1) * ITEMS_PER_PAGE;
    return filteredCodes.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredCodes, page]);

  // Check if any filters are active
  const hasActiveFilters = statusFilter !== "all" || !!debouncedSearch;

  // Fetch pending review count separately (for the alert badge)
  const { data: pendingCodes = [] } = useInternalGrossCodes({
    status: "pending_review",
    includeInactive: true,
  });
  const pendingCount = pendingCodes.length;

  const handleAdd = () => {
    setEditingCode(null);
    form.reset();
    open();
  };

  const handleEdit = (code: InternalGrossCodeWithUser) => {
    setEditingCode(code);
    form.setValues({
      code: code.code,
      description: code.description,
      scenario_description:
        (code as any).scenarioDescription ?? code.scenario_description ?? "",
      fragment_count_draft: getFragmentCountDisplayValue(
        (code as any).fragmentCount ?? code.fragment_count ?? null,
        (code as any).fragmentCountQualifier ??
          code.fragment_count_qualifier ??
          null
      ),
    });
    open();
  };

  const handleSubmit = async (values: GrossCodeFormValues) => {
    if (!userId) return;

    const parsed = parseAdminFragmentCountDraft(values.fragment_count_draft);
    if (!parsed.patch) return;
    const normalised = {
      code: values.code,
      description: values.description,
      scenario_description: values.scenario_description,
      fragment_count: parsed.patch.fragmentCount,
      fragment_count_qualifier: parsed.patch.fragmentCountQualifier,
    };

    try {
      if (editingCode) {
        await updateMutation.mutateAsync({
          id: editingCode.id,
          params: normalised,
        });
      } else {
        await createMutation.mutateAsync({
          ...normalised,
          created_by: userId,
        });
      }
      close();
      form.reset();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Failed to save code:", error);
    }
  };

  const handleFragmentCountDraftChange = useCallback(
    (value: string) => form.setFieldValue("fragment_count_draft", value),
    [form]
  );

  const handleFormSubmit = form.onSubmit(
    (values) => handleSubmit(values),
    () => scrollToFirstError(form)
  );

  const handleApprove = async (code: InternalGrossCodeWithUser) => {
    if (!userId) return;

    try {
      await approveMutation.mutateAsync({
        id: code.id,
        userId,
      });
      notifications.show({
        title: "Template Approved",
        message: `Gross code template "${code.code}" has been approved and is now active.`,
        color: "green",
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Failed to approve code:", error);
      notifications.show({
        title: "Approval Failed",
        message:
          "An error occurred while approving the template. Please try again.",
        color: "red",
      });
    }
  };

  const handleReviewClick = (code: InternalGrossCodeWithUser) => {
    setReviewingCode(code);
    setReviewMode("idle");
    rejectForm.reset();
    openReviewModal();
  };

  const handleReviewApprove = async () => {
    if (!reviewingCode) return;
    await handleApprove(reviewingCode);
    closeReviewModal();
    setReviewingCode(null);
    setReviewMode("idle");
  };

  const handleReviewRejectConfirm = async () => {
    if (!userId || !reviewingCode) return;

    // Validate reject editor content (plain text check)
    const rejectHtml = rejectEditor?.getHTML() || "";
    const hasContent =
      rejectHtml.trim() !== "" && rejectHtml.trim() !== "<p></p>";
    if (!hasContent) {
      setRejectReasonError("Rejection reason is required");
      return;
    }

    try {
      await rejectMutation.mutateAsync({
        id: reviewingCode.id,
        userId,
        reason: rejectHtml,
      });
      closeReviewModal();
      setReviewingCode(null);
      setReviewMode("idle");
      rejectForm.reset();
      notifications.show({
        title: "Template Rejected",
        message: `Gross code template "${reviewingCode.code}" has been rejected. The creator will be notified.`,
        color: "orange",
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Failed to reject code:", error);
      notifications.show({
        title: "Rejection Failed",
        message:
          "An error occurred while rejecting the template. Please try again.",
        color: "red",
      });
    }
  };

  const handleReviewRejectFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleReviewRejectConfirm();
  };

  const handleToggleActiveClick = (code: InternalGrossCodeWithUser) => {
    setTogglingCode(code);
    openToggleActiveModal();
  };

  const handleToggleActiveConfirm = async () => {
    if (!togglingCode) return;

    try {
      await updateMutation.mutateAsync({
        id: togglingCode.id,
        params: { is_active: !(togglingCode.is_active ?? true) },
      });
      closeToggleActiveModal();
      setTogglingCode(null);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Failed to toggle active status:", error);
    }
  };

  const handleClearFilters = () => {
    setStatusFilter("all");
    setPage(1);
  };

  const toggleRow = (codeId: string) => {
    setExpandedRow(expandedRow === codeId ? null : codeId);
  };

  const getStatusColor = (status: InternalGrossCodeStatus) => {
    switch (status) {
      case "pending_review":
        return "yellow";
      case "approved":
        return "green";
      case "rejected":
        return "red";
      default:
        return "gray";
    }
  };

  // Insert variable at cursor position in description field
  const insertVariable = (variableName: string) => {
    const variableText = ` {{${variableName}}} `;

    if (editor) {
      editor.chain().focus().insertContent(variableText).run();
    }
  };

  // Generate preview with sample data
  const previewDescription = useMemo(() => {
    if (!form.values.description) return "";
    return replaceVariables(form.values.description, SAMPLE_DATA);
  }, [form.values.description]);

  // Extract variables from description
  const extractedVariables = form.values.description
    ? parseVariables(form.values.description)
    : [];

  return (
    <>
      <Container fluid py="xl">
        <Box style={{ maxWidth: "1600px", margin: "0 auto" }}>
          <Stack gap="lg">
            {/* Header */}
            <Group justify="space-between" align="flex-start">
              <div>
                <Title order={2}>Internal Gross Codes</Title>
                <Text size="sm" c="dimmed" mt="xs">
                  Manage grossing templates with approval workflow
                </Text>
              </div>
              <Button leftSection={<IconPlus size={16} />} onClick={handleAdd}>
                Add New Template
              </Button>
            </Group>

            {/* Search and Filters Row */}
            <Group align="stretch" grow>
              {/* Search Section */}
              <Paper p="md" withBorder style={{ flex: 2 }}>
                <Stack gap="sm">
                  <Group>
                    <IconSearch size={20} />
                    <Text fw={500}>Search</Text>
                  </Group>
                  <TextInput
                    placeholder="Search by code, description, or scenario..."
                    value={search}
                    onChange={(e) => {
                      setSearch(e.target.value);
                      setPage(1);
                    }}
                    leftSection={<IconSearch size={16} />}
                  />
                </Stack>
              </Paper>

              {/* Filters Section */}
              <Paper p="md" withBorder style={{ flex: 1 }}>
                <Stack gap="sm">
                  <Group justify="space-between">
                    <Group>
                      <IconFilter size={20} />
                      <Text fw={500}>Filters</Text>
                    </Group>
                    <Button
                      variant="subtle"
                      size="compact-sm"
                      onClick={() => refetch()}
                      loading={isLoading}
                      leftSection={<IconRefresh size={16} />}
                    />
                  </Group>
                  <Select
                    placeholder="Filter by status..."
                    value={statusFilter}
                    onChange={(value) => {
                      setStatusFilter((value as StatusFilter) || "all");
                      setPage(1);
                    }}
                    data={[
                      { value: "all", label: "All Statuses" },
                      { value: "approved", label: "Approved" },
                      { value: "pending_review", label: "Pending Review" },
                      { value: "rejected", label: "Rejected" },
                    ]}
                  />
                  {hasActiveFilters && (
                    <Group gap="xs" wrap="wrap">
                      <Text size="sm" c="dimmed">
                        Active:
                      </Text>
                      <Badge variant="light" color="blue">
                        {statusFilter === "approved"
                          ? "Approved"
                          : statusFilter === "pending_review"
                          ? "Pending Review"
                          : "Rejected"}
                      </Badge>
                      <Button
                        variant="subtle"
                        size="compact-xs"
                        color="gray"
                        onClick={handleClearFilters}
                      >
                        Clear
                      </Button>
                    </Group>
                  )}
                  <Text size="sm" c="dimmed">
                    {filteredCodes.length} template
                    {filteredCodes.length !== 1 ? "s" : ""} found
                  </Text>
                </Stack>
              </Paper>
            </Group>

            {/* Pending Review Alert */}
            {pendingCount > 0 && (
              <Alert
                icon={<IconAlertCircle size={16} />}
                title="Pending Review"
                color="yellow"
              >
                There are {pendingCount} template
                {pendingCount !== 1 ? "s" : ""} waiting for approval.
              </Alert>
            )}

            {/* Table Section */}
            <Paper p="md" withBorder>
              <Stack gap="md">
                {/* Table Header with Pagination */}
                <Group justify="space-between">
                  <Text fw={500} size="lg">
                    Internal Gross Codes ({filteredCodes.length})
                  </Text>
                  {totalPages > 1 && (
                    <Group gap="md">
                      <Text size="sm" c="dimmed">
                        Showing {(page - 1) * ITEMS_PER_PAGE + 1}-
                        {Math.min(page * ITEMS_PER_PAGE, filteredCodes.length)}{" "}
                        of {filteredCodes.length}
                      </Text>
                      <Pagination
                        total={totalPages}
                        value={page}
                        onChange={setPage}
                        size="sm"
                      />
                    </Group>
                  )}
                </Group>

                {/* Table */}
                {isLoading ? (
                  <Center p="xl">
                    <Loader />
                  </Center>
                ) : filteredCodes.length === 0 ? (
                  <Text c="dimmed" ta="center" py="xl">
                    {hasActiveFilters || search
                      ? "No templates match the current filters"
                      : "No gross code templates found"}
                  </Text>
                ) : (
                  <Table striped highlightOnHover>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th style={{ width: 40 }} />
                        <Table.Th style={{ width: 120 }}>Code</Table.Th>
                        <Table.Th style={{ maxWidth: 400 }}>
                          Description
                        </Table.Th>
                        <Table.Th style={{ width: 120 }}>
                          Fragment Count
                        </Table.Th>
                        <Table.Th style={{ width: 160, whiteSpace: "nowrap" }}>
                          Status
                        </Table.Th>
                        <Table.Th style={{ width: 100 }}>Actions</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {paginatedData.map((code) => {
                        // Handle both snake_case (from API) and camelCase (from entity)
                        const isActive =
                          code.is_active ??
                          (code as unknown as { isActive?: boolean })
                            .isActive ??
                          true;
                        return (
                          <Fragment key={code.id}>
                            <Table.Tr style={{ opacity: isActive ? 1 : 0.5 }}>
                              {/* Expand Toggle */}
                              <Table.Td>
                                <ActionIcon
                                  variant="subtle"
                                  size="sm"
                                  onClick={() => toggleRow(code.id)}
                                >
                                  {expandedRow === code.id ? (
                                    <IconChevronDown size={16} />
                                  ) : (
                                    <IconChevronRight size={16} />
                                  )}
                                </ActionIcon>
                              </Table.Td>

                              {/* Code */}
                              <Table.Td>
                                <Text
                                  size="sm"
                                  fw={600}
                                  style={{ fontFamily: "monospace" }}
                                >
                                  {code.code}
                                </Text>
                              </Table.Td>

                              {/* Description */}
                              <Table.Td>
                                <Text size="sm" lineClamp={3}>
                                  {stripHtml(code.description)}
                                </Text>
                              </Table.Td>

                              {/* Fragment Count */}
                              <Table.Td>
                                <Text size="sm">
                                  {getFragmentCountDisplayValue(
                                    (code as any).fragmentCount ??
                                      code.fragment_count ??
                                      null,
                                    (code as any).fragmentCountQualifier ??
                                      code.fragment_count_qualifier ??
                                      null
                                  ) || "—"}
                                </Text>
                              </Table.Td>

                              {/* Status */}
                              <Table.Td>
                                <Badge
                                  size="sm"
                                  variant="light"
                                  color={getStatusColor(code.status)}
                                >
                                  {code.status.replace("_", " ")}
                                </Badge>
                              </Table.Td>

                              {/* Actions */}
                              <Table.Td>
                                <Group gap="xs" wrap="nowrap">
                                  <Tooltip label="Edit">
                                    <ActionIcon
                                      variant="subtle"
                                      color="blue"
                                      onClick={() => handleEdit(code)}
                                    >
                                      <IconEdit size={18} />
                                    </ActionIcon>
                                  </Tooltip>
                                  {code.status === "pending_review" && (
                                    <Tooltip label="Review">
                                      <ActionIcon
                                        variant="subtle"
                                        color="orange"
                                        onClick={() => handleReviewClick(code)}
                                      >
                                        <IconEye size={18} />
                                      </ActionIcon>
                                    </Tooltip>
                                  )}
                                  <Tooltip
                                    label={isActive ? "Deactivate" : "Activate"}
                                  >
                                    <ActionIcon
                                      variant="subtle"
                                      color={isActive ? "red" : "green"}
                                      onClick={() =>
                                        handleToggleActiveClick(code)
                                      }
                                    >
                                      {isActive ? (
                                        <IconX size={18} />
                                      ) : (
                                        <IconCheck size={18} />
                                      )}
                                    </ActionIcon>
                                  </Tooltip>
                                </Group>
                              </Table.Td>
                            </Table.Tr>

                            {/* Expanded Row - Details */}
                            {expandedRow === code.id && (
                              <Table.Tr>
                                <Table.Td colSpan={5} p={0}>
                                  <Box
                                    pl="xl"
                                    pr="md"
                                    pb="md"
                                    pt="sm"
                                    bg="gray.0"
                                  >
                                    {/* Scenario Description */}
                                    {((code as any).scenarioDescription ||
                                      code.scenario_description) && (
                                      <Stack gap={4} mb="md">
                                        <Text size="xs" c="dimmed" fw={500}>
                                          Scenario
                                        </Text>
                                        <Text size="sm">
                                          {(code as any).scenarioDescription ||
                                            code.scenario_description}
                                        </Text>
                                      </Stack>
                                    )}

                                    {/* Template Variables */}
                                    {parseVariables(code.description).length >
                                      0 && (
                                      <Stack gap={4} mb="md">
                                        <Text size="xs" c="dimmed" fw={500}>
                                          Template Variables
                                        </Text>
                                        <Group gap="xs">
                                          {parseVariables(code.description).map(
                                            (variable) => (
                                              <Code key={variable} fz="sm">
                                                {`{{${variable}}}`}
                                              </Code>
                                            )
                                          )}
                                        </Group>
                                      </Stack>
                                    )}

                                    {/* Full Description Preview */}
                                    <Stack gap={4} mb="md">
                                      <Text size="xs" c="dimmed" fw={500}>
                                        Full Description
                                      </Text>
                                      <Paper withBorder p="sm" bg="gray.0">
                                        <Box
                                          style={{ fontSize: "14px" }}
                                          dangerouslySetInnerHTML={{
                                            __html: replaceVariables(
                                              code.description,
                                              SAMPLE_DATA
                                            ),
                                          }}
                                        />
                                      </Paper>
                                    </Stack>

                                    <Group gap="xl" align="flex-start">
                                      {/* Created By - only show if present */}
                                      {code.created_by_user?.full_name && (
                                        <Stack gap={4}>
                                          <Text size="xs" c="dimmed" fw={500}>
                                            Created By
                                          </Text>
                                          <Text size="sm">
                                            {code.created_by_user.full_name}
                                          </Text>
                                        </Stack>
                                      )}

                                      {/* Reviewed By - only show if present */}
                                      {code.reviewed_by_user?.full_name && (
                                        <Stack gap={4}>
                                          <Text size="xs" c="dimmed" fw={500}>
                                            Reviewed By
                                          </Text>
                                          <Text size="sm">
                                            {code.reviewed_by_user.full_name}
                                          </Text>
                                        </Stack>
                                      )}

                                      {/* Rejection Reason (only if rejected) */}
                                      {code.status === "rejected" &&
                                        code.rejection_reason && (
                                          <Stack gap={4} style={{ flex: 1 }}>
                                            <Text size="xs" c="dimmed" fw={500}>
                                              Rejection Reason
                                            </Text>
                                            <Text size="sm" c="red">
                                              {code.rejection_reason}
                                            </Text>
                                          </Stack>
                                        )}
                                    </Group>
                                  </Box>
                                </Table.Td>
                              </Table.Tr>
                            )}
                          </Fragment>
                        );
                      })}
                    </Table.Tbody>
                  </Table>
                )}
              </Stack>
            </Paper>
          </Stack>
        </Box>
      </Container>

      {/* Create/Edit Modal */}
      <Modal
        opened={opened}
        onClose={() => {
          close();
          setShowPreview(false);
        }}
        title={
          editingCode
            ? "Edit Gross Code Template"
            : "Add New Gross Code Template"
        }
        size="xl"
      >
        <form onSubmit={handleFormSubmit}>
          <Stack gap="md">
            <TextInput
              label="Code"
              placeholder="e.g., SBX-001"
              withAsterisk
              {...form.getInputProps("code")}
            />

            <AdminFragmentCountInput
              value={form.values.fragment_count_draft}
              onChange={handleFragmentCountDraftChange}
              error={form.errors.fragment_count_draft}
            />

            <Textarea
              label="Scenario Description"
              placeholder="Brief description of when to use this template"
              minRows={2}
              {...form.getInputProps("scenario_description")}
              description="Helps users find the right template quickly"
            />

            {/* Available Variables Helper */}
            <Paper withBorder p="sm">
              <Stack gap="xs">
                <Text size="sm" fw={600}>
                  Available Variables (click to insert)
                </Text>
                <Group gap={6} wrap="wrap">
                  {AVAILABLE_VARIABLES.map((variable) => (
                    <Tooltip
                      key={variable.name}
                      label={`${variable.description} (e.g., "${variable.example}")`}
                      position="top"
                      withArrow
                    >
                      <Badge
                        variant="light"
                        color="indigo"
                        size="md"
                        style={{ cursor: "pointer", fontFamily: "monospace" }}
                        onClick={() => insertVariable(variable.name)}
                      >
                        {`{{${variable.name}}}`}
                      </Badge>
                    </Tooltip>
                  ))}
                </Group>
              </Stack>
            </Paper>

            {/* Rich Text Description Template */}
            <div>
              <Text component="label" fz="sm" fw={500} mb={5}>
                Description Template{" "}
                <Text span c="red">
                  *
                </Text>
              </Text>
              <Text size="xs" c="dimmed" mb="xs">
                {
                  "Use {{variable_name}} syntax for dynamic fields. Supports bold, italic, underline, and lists."
                }
              </Text>
              <RichTextEditor editor={editor}>
                <RichTextEditor.Toolbar sticky stickyOffset={60}>
                  <RichTextEditor.ControlsGroup>
                    <RichTextEditor.Bold />
                    <RichTextEditor.Italic />
                    <RichTextEditor.Underline />
                  </RichTextEditor.ControlsGroup>

                  <RichTextEditor.ControlsGroup>
                    <RichTextEditor.BulletList />
                    <RichTextEditor.OrderedList />
                  </RichTextEditor.ControlsGroup>

                  <RichTextEditor.ControlsGroup>
                    <RichTextEditor.ClearFormatting />
                  </RichTextEditor.ControlsGroup>
                </RichTextEditor.Toolbar>

                <RichTextEditor.Content style={{ minHeight: "200px" }} />
              </RichTextEditor>
              {form.errors.description && (
                <Text size="xs" c="red" mt={5}>
                  {form.errors.description}
                </Text>
              )}
            </div>

            {/* Detected Variables & Preview Toggle */}
            {extractedVariables.length > 0 && (
              <Paper withBorder p="sm" bg="blue.0">
                <Stack gap="xs">
                  <Group justify="space-between">
                    <Text size="xs" fw={600} c="blue">
                      Detected Variables ({extractedVariables.length}):
                    </Text>
                    <Button
                      variant="subtle"
                      size="compact-xs"
                      onClick={() => setShowPreview(!showPreview)}
                    >
                      {showPreview ? "Hide Preview" : "Show Preview"}
                    </Button>
                  </Group>
                  <Group gap="xs">
                    {extractedVariables.map((variable) => (
                      <Code key={variable}>{`{{${variable}}}`}</Code>
                    ))}
                  </Group>
                  <Text size="xs" c="dimmed">
                    These will be replaced with input fields during grossing
                  </Text>
                </Stack>
              </Paper>
            )}

            {/* Preview Section */}
            {showPreview && form.values.description && (
              <Paper withBorder p="sm" bg="gray.0">
                <Stack gap="xs">
                  <Text size="xs" fw={600} c="dimmed">
                    Preview (with sample data):
                  </Text>
                  <Box
                    className="rich-text-preview"
                    style={{ fontSize: "14px" }}
                    dangerouslySetInnerHTML={{ __html: previewDescription }}
                  />
                </Stack>
              </Paper>
            )}

            <Divider />

            {/* Actions */}
            <Group justify="flex-end">
              <Button variant="subtle" onClick={close}>
                Cancel
              </Button>
              <Button
                type="submit"
                loading={createMutation.isPending || updateMutation.isPending}
              >
                {editingCode ? "Update" : "Create"}
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>

      {/* Review Modal */}
      <Modal.Root
        opened={reviewModalOpened}
        onClose={() => {
          closeReviewModal();
          setReviewingCode(null);
          setReviewMode("idle");
          rejectForm.reset();
        }}
        size="xl"
        closeOnClickOutside={false}
        styles={{
          content: {
            display: "flex",
            flexDirection: "column",
            maxHeight: "85vh",
          },
        }}
      >
        <Modal.Overlay />
        <Modal.Content>
          <Modal.Header>
            <Modal.Title>Review Gross Code Template</Modal.Title>
            <Modal.CloseButton />
          </Modal.Header>

          <Modal.Body style={{ flex: 1, overflow: "auto" }}>
            <Stack gap="md">
              {reviewingCode && (
                <Stack gap="sm">
                  {/* Code & Submitter */}
                  <Paper withBorder p="md" bg="gray.0">
                    <Group
                      justify="space-between"
                      align="flex-start"
                      wrap="nowrap"
                    >
                      <Stack gap={4}>
                        <Text size="xs" tt="uppercase" fw={700} c="dimmed">
                          Code
                        </Text>
                        <Code fz="md" fw={700}>
                          {reviewingCode.code}
                        </Code>
                      </Stack>
                      {reviewingCode.created_by_user && (
                        <Stack gap={4} style={{ textAlign: "right" }}>
                          <Text size="xs" tt="uppercase" fw={700} c="dimmed">
                            Submitted by
                          </Text>
                          <Text size="sm">
                            {reviewingCode.created_by_user.full_name}
                          </Text>
                        </Stack>
                      )}
                    </Group>
                  </Paper>

                  {/* Description (full rich text) */}
                  <Stack gap={6}>
                    <Text size="xs" tt="uppercase" fw={700} c="dimmed">
                      Gross Description Template
                    </Text>
                    <Paper withBorder p="md">
                      <Box
                        className="tiptap-readonly-content"
                        style={{ fontSize: "14px", lineHeight: 1.6 }}
                        dangerouslySetInnerHTML={{
                          __html: reviewingCode.description,
                        }}
                      />
                    </Paper>
                  </Stack>

                  {/* Scenario Description */}
                  {reviewingCode.scenario_description && (
                    <Stack gap={6}>
                      <Text size="xs" tt="uppercase" fw={700} c="dimmed">
                        When to Use (Scenario)
                      </Text>
                      <Paper withBorder p="md">
                        <Text size="sm" style={{ whiteSpace: "pre-wrap" }}>
                          {reviewingCode.scenario_description}
                        </Text>
                      </Paper>
                    </Stack>
                  )}
                </Stack>
              )}

              {/* Rejection reason editor — shown only in rejecting mode */}
              {reviewMode === "rejecting" && (
                <form id="reject-form" onSubmit={handleReviewRejectFormSubmit}>
                  <Stack gap="sm">
                    <Text size="sm" fw={500}>
                      Rejection Reason{" "}
                      <Text component="span" c="red">
                        *
                      </Text>
                    </Text>
                    <RichTextEditor
                      editor={rejectEditor}
                      style={{
                        border: rejectReasonError
                          ? "1px solid var(--mantine-color-red-6)"
                          : undefined,
                        borderRadius: 4,
                        minHeight: 120,
                      }}
                    >
                      <RichTextEditor.Toolbar sticky>
                        <RichTextEditor.ControlsGroup>
                          <RichTextEditor.Bold />
                          <RichTextEditor.Italic />
                          <RichTextEditor.Underline />
                        </RichTextEditor.ControlsGroup>
                        <RichTextEditor.ControlsGroup>
                          <RichTextEditor.BulletList />
                          <RichTextEditor.OrderedList />
                        </RichTextEditor.ControlsGroup>
                      </RichTextEditor.Toolbar>
                      <RichTextEditor.Content />
                    </RichTextEditor>
                    {rejectReasonError && (
                      <Text size="xs" c="red">
                        {rejectReasonError}
                      </Text>
                    )}
                    <Text size="xs" c="dimmed">
                      This will be visible to the creator
                    </Text>
                  </Stack>
                </form>
              )}
            </Stack>
          </Modal.Body>

          {/* Footer — outside Modal.Body */}
          <Divider />
          {reviewMode === "idle" ? (
            <Group justify="space-between" p="md">
              <Button
                variant="subtle"
                color="gray"
                onClick={() => {
                  closeReviewModal();
                  setReviewingCode(null);
                  setReviewMode("idle");
                  rejectForm.reset();
                }}
              >
                Cancel
              </Button>
              <Group gap="sm">
                <Button
                  color="red"
                  variant="light"
                  leftSection={<IconX size={16} />}
                  onClick={() => setReviewMode("rejecting")}
                >
                  Reject
                </Button>
                <Button
                  color="green"
                  leftSection={<IconCheck size={16} />}
                  onClick={handleReviewApprove}
                  loading={approveMutation.isPending}
                >
                  Approve
                </Button>
              </Group>
            </Group>
          ) : (
            <Group justify="space-between" p="md">
              <Button
                variant="subtle"
                onClick={() => {
                  setReviewMode("idle");
                  rejectForm.reset();
                }}
              >
                Back
              </Button>
              <Button
                color="red"
                form="reject-form"
                type="submit"
                loading={rejectMutation.isPending}
              >
                Reject Template
              </Button>
            </Group>
          )}
        </Modal.Content>
      </Modal.Root>

      {/* Toggle Active Confirmation Modal */}
      <Modal
        opened={toggleActiveModalOpened}
        onClose={() => {
          closeToggleActiveModal();
          setTogglingCode(null);
        }}
        title={
          togglingCode?.is_active ?? true
            ? "Deactivate Gross Code"
            : "Activate Gross Code"
        }
        size="sm"
      >
        <Stack gap="md">
          {togglingCode && (
            <Paper withBorder p="sm" bg="gray.0">
              <Text size="sm" fw={600}>
                {togglingCode.code}
              </Text>
              <Text size="xs" c="dimmed" lineClamp={2}>
                {stripHtml(togglingCode.description)}
              </Text>
            </Paper>
          )}

          <Text size="sm">
            {togglingCode?.is_active ?? true
              ? "Are you sure you want to deactivate this gross code? It will no longer be available for selection in the grossing workflow."
              : "Are you sure you want to activate this gross code? It will become available for selection in the grossing workflow."}
          </Text>

          <Group justify="flex-end">
            <Button
              variant="subtle"
              onClick={() => {
                closeToggleActiveModal();
                setTogglingCode(null);
              }}
            >
              Cancel
            </Button>
            <Button
              color={togglingCode?.is_active ?? true ? "red" : "green"}
              onClick={handleToggleActiveConfirm}
              loading={updateMutation.isPending}
            >
              {togglingCode?.is_active ?? true ? "Deactivate" : "Activate"}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
